import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, Subscription, interval, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';

export interface PassportJobStatus {
    jobId: number;
    status: 'PENDING' | 'RUNNING' | 'DONE' | 'ERROR' | 'QUARANTINED';
    progress: number;
    message?: string;
    error?: string;
    errorCategory?: string;
    resultJson?: string;
    updatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class PassportJobLiveService {
    private readonly status$ = new Subject<PassportJobStatus>();
    private pollSub?: Subscription;

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly http: HttpClient,
    ) {}

    listJobs(take = 200): Observable<unknown> {
        return this.http.get(`${this.apiBaseUrl}rinderpassscan/passport_jobs.php`, {
            params: { take: take.toString() },
        });
    }

    connect(jobId: number): Observable<PassportJobStatus> {
        this.disconnect();
        this.startPolling(jobId);
        return this.status$.asObservable();
    }

    disconnect() {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = undefined;
        }
    }

    getSourcePdfBlob(grpsId: number): Observable<Blob> {
        return this.http.get(`${this.apiBaseUrl}rinderpassscan/passport_job_source_pdf.php`, {
            params: { grps_id: grpsId.toString() },
            responseType: 'blob',
        });
    }

    private startPolling(jobId: number) {
        this.pollSub = interval(2000)
            .pipe(
                switchMap(() =>
                    this.http
                        .get<any>(`${this.apiBaseUrl}rinderpassscan/passport_job.php`, {
                            params: { job_id: jobId.toString() },
                        })
                        .pipe(catchError(() => of(null))),
                ),
            )
            .subscribe(job => {
                if (!job) {
                    return;
                }

                const payload: PassportJobStatus = {
                    jobId: job.jobId ?? job.job_id ?? job.id ?? job.cpj_id,
                    status: job.status,
                    progress: job.progress ?? job.progressPercent ?? job.progress_percent ?? 0,
                    message: job.message ?? job.messageText ?? job.message_text,
                    error: job.error ?? job.errorText ?? job.error_text,
                    errorCategory: job.errorCategory ?? job.error_category,
                    resultJson: job.resultJson ?? job.result_json,
                    updatedAt: job.updatedAt ?? job.updated_at,
                };
                this.status$.next(payload);
                if (payload.status === 'DONE' || payload.status === 'ERROR' || payload.status === 'QUARANTINED') {
                    this.disconnect();
                }
            });
    }
}
